// Import 3K Darts par copier-coller -- SECOURS de l'import par lien (lu côté serveur, cf.
// Dartshub/backend/modules/api/leaderboards/threek_darts.py), si 3K change un jour le
// filtrage de son API. L'admin copie la réponse JSON du classement final depuis son
// navigateur (portail 3K, F12 > Réseau > requête "placement" > Réponse) et la colle ici.
// Mêmes règles que le serveur (noms, codes CLT, poules) : garder les deux alignés.
import { getCodeClt, type ImportRow } from './types'

interface ThreeKPlacement {
  place?: string | null // "17." -- ex aequo : même place pour toute la tranche
  placeIndex?: number | null // 1..N, unique
  participant?: {
    displayName?: string | null // "NOM, Prénom"
    deregistered?: boolean
  } | null
  phase?: { modeCd?: string | null } | null // "ko" (tableau) ou "group" (sorti en poules)
}

// "GEORGE, Dave" -> "Dave GEORGE" ; une doublette ("GEORGE, Dave & PIERRE, Cyril") est
// convertie joueur par joueur, au format "Nom1 / Nom2" attendu par le pipeline d'import.
export function threeKNameToRaw(displayName: string): string {
  return displayName
    .split(/[&/]/)
    .map((part) => {
      const [last, ...first] = part.split(',')
      return first.length ? `${first.join(',').trim()} ${last.trim()}` : part.trim()
    })
    .filter(Boolean)
    .join(' / ')
}

// Sortis en poules : le barème compte les victoires (P0..P7), que le classement 3K ne
// donne pas (seulement la place dans la tranche, "25." / "37.") -- code laissé vide, à
// compléter par l'admin (l'aperçu bloque tant qu'il manque).
export function parseThreeKPlacements(
  text: string,
  category: string,
  listType: 'A' | 'B'
): { rows: ImportRow[]; poolCount: number } {
  let data: unknown
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error('Texte collé illisible : JSON attendu (réponse de la requête "placement").')
  }
  if (!Array.isArray(data)) throw new Error('JSON inattendu : liste du classement ("placement") attendue.')

  const rows: ImportRow[] = []
  let poolCount = 0
  for (const entry of data as ThreeKPlacement[]) {
    const name = entry?.participant?.displayName?.trim()
    if (!name || entry.participant?.deregistered) continue
    const pos = parseInt(entry.place ?? '', 10) || entry.placeIndex
    if (!pos) continue
    const inPools = entry.phase?.modeCd === 'group'
    if (inPools) poolCount++
    rows.push({
      category,
      player_name_raw: threeKNameToRaw(name),
      code_clt: inPools ? '' : getCodeClt(pos, 'AB', listType),
    })
  }
  return { rows, poolCount }
}

// Plusieurs réponses JSON collées à la suite ({...}{...}) : la page "tous les groupes"
// (".../phase/{id}/round/0") les donne d'un coup, mais une réponse par groupe reste acceptée.
function splitJsonDocuments(text: string): string[] {
  const docs: string[] = []
  let depth = 0
  let start = -1
  let inString = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inString) {
      if (c === '\\') i++
      else if (c === '"') inString = false
    } else if (c === '"') {
      inString = true
    } else if (c === '{' || c === '[') {
      if (depth++ === 0) start = i
    } else if (c === '}' || c === ']') {
      if (--depth === 0) docs.push(text.slice(start, i + 1))
    }
  }
  return docs
}

interface ThreeKGroupTable {
  tableInfo?: {
    tableEntries?: {
      tableEntries?: { participantName?: string | null; win?: number | null }[] | null
    }[] | null
  } | null
}

// Victoires de poules par équipe/joueur (nom déjà converti, cf. threeKNameToRaw), lues
// dans `tableInfo.tableEntries` (un élément par groupe) d'une réponse de page de poules.
export function parseThreeKGroupWins(text: string): Map<string, number> {
  const wins = new Map<string, number>()
  for (const doc of splitJsonDocuments(text)) {
    let data: ThreeKGroupTable
    try {
      data = JSON.parse(doc)
    } catch {
      throw new Error('Texte collé illisible : JSON attendu (réponse de la page du groupe).')
    }
    for (const group of data?.tableInfo?.tableEntries ?? []) {
      for (const entry of group.tableEntries ?? []) {
        const name = entry.participantName?.trim()
        if (name && typeof entry.win === 'number') wins.set(threeKNameToRaw(name), entry.win)
      }
    }
  }
  if (!wins.size) throw new Error('Aucun classement de poule trouvé dans le texte collé.')
  return wins
}
